import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { first, tap, catchError, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { UserWelcomeMessage } from '@shared/entities/userWelcomeMessage';
import { UserWelcomeMessageService } from '@shared/services/api/user-welcome-message.service';
import { AvailableLanguages, LanguageService } from '@shared/services';
import { TabItem } from '@shared/components/icam-tabs/icam-tabs.component';

@Component({
  selector: 'app-user-welcome-message-form',
  templateUrl: './user-welcome-message-form.component.html',
  styleUrl: './user-welcome-message-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserWelcomeMessageFormComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private welcomeMessageService = inject(UserWelcomeMessageService);
  private translateService = inject(TranslateService);
  private languageService = inject(LanguageService);

  @Output() closeForm = new EventEmitter<void>();

  messageForm: FormGroup;
  isLoading = false;
  isSaving = false;
  welcomeMessages = new Map<string, UserWelcomeMessage>();
  showSuccess = false;
  showError = false;
  errorMessage = '';

  languageTabs: TabItem[] = [];
  selectedLanguage = 'it';
  availableLanguages: AvailableLanguages[] = [];

  constructor() {
    this.messageForm = this.fb.group({
      id: [''],
      subject: ['', [Validators.maxLength(200)]],
      message: ['', [Validators.required, Validators.maxLength(1000)]]
    });
  }

  ngOnInit(): void {
    this.initializeLanguageTabs();
    this.loadWelcomeMessage();
  }

  initializeLanguageTabs(): void {
    this.availableLanguages = this.languageService.getAvailableLanguages();
    this.languageTabs = this.availableLanguages.map((lang) => ({
      id: lang.id,
      label: lang.description.toUpperCase(),
      needsTranslation: false
    }));
    this.selectedLanguage = this.availableLanguages[0]?.id || 'it';
    this._cdr.markForCheck();
  }

  onTabChange(tab: TabItem): void {
    this.saveCurrentFormToMap();
    this.selectedLanguage = tab.id as string;
    this.loadWelcomeMessage();
    this._cdr.markForCheck();
  }

  private saveCurrentFormToMap(): void {
    const formData = this.messageForm.getRawValue();
    const currentMessage: UserWelcomeMessage = {
      id: formData.id || undefined,
      subject: formData.subject || '',
      message: formData.message || '',
      lang: this.selectedLanguage
    };
    this.welcomeMessages.set(this.selectedLanguage, currentMessage);
  }

  loadWelcomeMessage(): void {
    if (this.welcomeMessages.has(this.selectedLanguage)) {
      const cachedMessage = this.welcomeMessages.get(this.selectedLanguage)!;
      this.updateForm(cachedMessage);
      return;
    }

    this.isLoading = true;
    this._cdr.markForCheck();
    this.welcomeMessageService
      .getWelcomeMessage(this.selectedLanguage)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (message) => {
          this.welcomeMessages.set(this.selectedLanguage, message);
          this.updateForm(message);
          this.isLoading = false;
          this._cdr.markForCheck();
        },
        error: (error) => {
          this.showError = true;
          this.errorMessage = this.extractErrorMessage(error);
          this.isLoading = false;
          this._cdr.markForCheck();
        }
      });
  }

  private updateForm(message: UserWelcomeMessage): void {
    this.messageForm.patchValue({
      id: message.id || '',
      subject: message.subject || '',
      message: message.message || ''
    });
    this.messageForm.markAsPristine();
    this._cdr.markForCheck();
  }

  saveMessage(): void {
    if (this.messageForm.invalid) {
      this.markAllFieldsAsTouched();
      return;
    }

    this.saveCurrentFormToMap();

    this.isSaving = true;
    this.showSuccess = false;
    this.showError = false;

    const formData = this.messageForm.getRawValue();
    const messageData: UserWelcomeMessage = {
      id: formData.id || undefined,
      subject: formData.subject || '',
      message: formData.message || '',
      lang: this.selectedLanguage
    };

    this.welcomeMessageService
      .saveWelcomeMessage(messageData)
      .pipe(
        first(),
        takeUntilDestroyed(this._destroyRef),
        tap((response: any) => {
          this.isSaving = false;

          if (response && (response.error || response.validation)) {
            this.showError = true;
            this.errorMessage = this.extractErrorMessage(response);
            this._cdr.markForCheck();
            return;
          }

          if (response) {
            this.welcomeMessages.set(this.selectedLanguage, {
              ...messageData,
              id: response.id || messageData.id,
              createdDate: response.createdDate
            });

            this.messageForm.patchValue({
              id: response.id || formData.id
            });
            this.messageForm.markAsPristine();
          }

          this.showSuccess = true;
          this._cdr.markForCheck();
          setTimeout(() => {
            this.showSuccess = false;
            this._cdr.markForCheck();
          }, 3000);
        }),
        catchError((error) => {
          this.isSaving = false;
          this.showError = true;
          this.errorMessage = this.extractErrorMessage(error?.error);
          this._cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  resetForm(): void {
    const currentMessage = this.welcomeMessages.get(this.selectedLanguage);
    if (currentMessage) {
      this.messageForm.patchValue({
        id: currentMessage.id || '',
        subject: currentMessage.subject || '',
        message: currentMessage.message || ''
      });
    } else {
      this.messageForm.reset({
        id: '',
        subject: '',
        message: ''
      });
    }
    this.messageForm.markAsPristine();
    this.showSuccess = false;
    this.showError = false;
    this._cdr.markForCheck();
  }

  cancel(): void {
    if (this.messageForm.dirty) {
      this.saveCurrentFormToMap();
    }
    this.closeForm.emit();
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.messageForm.controls).forEach((key) => {
      this.messageForm.get(key)?.markAsTouched();
    });
    this._cdr.markForCheck();
  }

  private extractErrorMessage(response: any): string {
    if (!response) {
      return this.translateService.instant('COMMON.ERROR_MESSAGE');
    }

    if (response.message) {
      return response.message;
    }

    if (response.validation && response.validation.fields && response.validation.fields.length > 0) {
      const validationErrors = response.validation.fields
        .map((field: any) => `${field.field}: ${field.error}`)
        .join('\n');

      return `${this.translateService.instant('VALIDATION.ERRORS')}:\n${validationErrors}`;
    }

    if (response.error && typeof response.error === 'string') {
      return response.error;
    }

    return this.translateService.instant('COMMON.ERROR_MESSAGE');
  }

  hasContentForLanguage(langId: string): boolean {
    const message = this.welcomeMessages.get(langId);
    return !!(message && (message.message || message.subject));
  }
}
